import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PageLayout from '../components/PageLayout';
import AccountCard from '../components/AccountCard';
import Button from '../components/ui/Button';
import PageHeader from '../components/ui/PageHeader';
import PageBody from '../components/ui/PageBody';
import PageMain from '../components/ui/PageMain';
import { getAdminPlugins, getPluginSettings, setPluginSettings } from '../api';
import { pluginText } from '../plugins/pluginFrame';

const SECRET_MASK = '••••••';

/**
 * /admin/plugins/:id: a plugin's settings for one level - global, or one
 * department (?department=<id>).
 *
 * Each field holds this level's own value; left empty, the next level applies
 * (department → global → the plugin's default), and the value that applies
 * now is the field's placeholder. Clearing a department's field goes back to
 * the global value. Secrets come back masked and stay as they are unless a new
 * one is typed.
 */
function AdminPluginSettingsPage() {
  const { t, i18n } = useTranslation();
  const { pluginId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const departmentId = searchParams.get('department') ? Number(searchParams.get('department')) : null;

  const [plugin, setPlugin] = useState(null);
  const [scope, setScope] = useState(null);
  const [fields, setFields] = useState(null);
  const [values, setValues] = useState({});
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  const lang = i18n.language;

  const load = useCallback(() => {
    setFields(null);
    setMessage(null);
    Promise.all([getAdminPlugins(), getPluginSettings(pluginId, departmentId)])
      .then(([list, result]) => {
        setScope(list.scope);
        setPlugin(list.plugins.find((p) => p.id === pluginId) ?? null);
        setFields(result.settings);
        setValues(Object.fromEntries(result.settings.map((s) => [s.key, s.value ?? ''])));
      })
      .catch((err) => setMessage({ type: 'error', text: err?.message || t('adminPlugins.loadError') }));
  }, [pluginId, departmentId, t]);

  useEffect(load, [load]);

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const sent = Object.fromEntries(
        fields.map((field) => [field.key, values[field.key] === '' ? null : values[field.key]]),
      );
      const result = await setPluginSettings(pluginId, departmentId, sent);
      setFields(result.settings);
      setValues(Object.fromEntries(result.settings.map((s) => [s.key, s.value ?? ''])));
      setMessage({ type: 'success', text: t('adminPlugins.saved') });
    } catch (err) {
      const text = Array.isArray(err?.data?.message) ? err.data.message.join(' · ') : err?.message;
      setMessage({ type: 'error', text: text || t('adminPlugins.saveError') });
    } finally {
      setSaving(false);
    }
  }

  const sourceText = (field) =>
    ({
      department: t('adminPlugins.sourceDepartment'),
      global: t('adminPlugins.sourceGlobal'),
      default: t('adminPlugins.sourceDefault'),
      none: t('adminPlugins.sourceNone'),
    })[field.source];

  const placeholder = (field) => {
    if (field.effective === null || field.effective === undefined) return t('adminPlugins.sourceNone');
    if (field.type === 'boolean') {
      return `${field.effective === 'true' ? t('adminPlugins.yes') : t('adminPlugins.no')} (${sourceText(field)})`;
    }
    if (field.type === 'select') {
      const option = field.options?.find((o) => o.value === field.effective);
      return `${pluginText(option?.label, lang) || field.effective} (${sourceText(field)})`;
    }
    return `${field.effective} (${sourceText(field)})`;
  };

  const input = (field) => {
    const id = `plugin-setting-${field.key}`;
    const common = {
      id,
      name: field.key,
      value: values[field.key] ?? '',
      disabled: saving,
      onChange: (event) => setValues((current) => ({ ...current, [field.key]: event.target.value })),
    };

    if (field.type === 'select' || field.type === 'boolean') {
      const options = field.type === 'boolean'
        ? [{ value: 'true', label: t('adminPlugins.yes') }, { value: 'false', label: t('adminPlugins.no') }]
        : field.options.map((option) => ({ value: option.value, label: pluginText(option.label, lang) || option.value }));
      return (
        <select {...common}>
          <option value="">{placeholder(field)}</option>
          {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      );
    }

    return (
      <input
        {...common}
        type={{ secret: 'password', number: 'number', url: 'url' }[field.type] ?? 'text'}
        placeholder={field.type === 'secret' && field.effective === SECRET_MASK ? t('adminPlugins.secretSet') : placeholder(field)}
        autoComplete="off"
      />
    );
  };

  const levelName = departmentId === null
    ? t('adminPlugins.levelGlobal')
    : scope?.departments.find((d) => d.department_id === departmentId)?.name ?? String(departmentId);

  return (
    <PageLayout
      extraSidebarItems={[{ to: '/admin/plugins', label: t('adminPlugins.title'), icon: 'fa-solid fa-puzzle-piece' }]}
    >
      <PageMain>
        <PageHeader
          eyebrow={t('adminPlugins.title')}
          title={pluginText(plugin?.name, lang) || pluginId}
          titleId="admin-plugin-settings-title"
          description={t('adminPlugins.settingsDescription')}
        />

        <PageBody className="account-page" aria-labelledby="admin-plugin-settings-title">
          <div className="account-settings-column admin-settings-column">
            <AccountCard
              titleId="admin-plugin-settings-card"
              isFirst
              title={t('adminPlugins.settingsFor', { level: levelName })}
              icon="fa-solid fa-sliders"
            >
              {scope && (
                <div className="account-field-row">
                  <label htmlFor="plugin-settings-level">{t('adminPlugins.level')}</label>
                  <select
                    id="plugin-settings-level"
                    value={departmentId ?? ''}
                    onChange={(event) => setSearchParams(event.target.value ? { department: event.target.value } : {})}
                  >
                    {scope.platform && <option value="">{t('adminPlugins.levelGlobal')}</option>}
                    {scope.departments.map((d) => (
                      <option key={d.department_id} value={d.department_id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {message && (
                <div className={`login-message login-message-${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>
                  <i className={`fa-solid ${message.type === 'error' ? 'fa-circle-exclamation' : 'fa-circle-check'}`} />
                  <span>{message.text}</span>
                </div>
              )}

              {fields && (
                <form onSubmit={save}>
                  <p className="plugin-settings-hint">
                    {t(departmentId === null ? 'adminPlugins.hintGlobal' : 'adminPlugins.hintDepartment')}
                  </p>

                  {fields.map((field) => (
                    <div className="account-field-row" key={field.key}>
                      <label htmlFor={`plugin-setting-${field.key}`}>{pluginText(field.label, lang) || field.key}</label>
                      <div className="plugin-setting-field">
                        {input(field)}
                        {values[field.key] !== '' && (
                          <button
                            type="button"
                            className="plugin-setting-clear"
                            disabled={saving}
                            onClick={() => setValues((current) => ({ ...current, [field.key]: '' }))}
                          >
                            {t(departmentId === null ? 'adminPlugins.clearGlobal' : 'adminPlugins.clearDepartment')}
                          </button>
                        )}
                        {field.help && <small>{pluginText(field.help, lang)}</small>}
                      </div>
                    </div>
                  ))}

                  <div className="article-editor-actions">
                    <Button to="/admin/plugins">{t('adminPlugins.back')}</Button>
                    <Button variant="primary" type="submit" disabled={saving}>
                      {saving ? t('adminPlugins.saving') : t('adminPlugins.save')}
                    </Button>
                  </div>
                </form>
              )}
            </AccountCard>
          </div>
        </PageBody>
      </PageMain>
    </PageLayout>
  );
}

export default AdminPluginSettingsPage;
